<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-flex>
        <dt-search-box @typing="searchValue" @reset-datatable="resetDataTable()" @clear-search-field="search = null" />
      </v-flex>
    </v-card>

    <v-card flat>
      <v-layout row>
        <v-layout row wrap justify-start align-end>
          <v-flex pa-2>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin:0 10px 0; height: 30px; width: 30px;"
                  :loading="loadThePrintBtn"
                  flat
                  fab
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('print')"
                >
                  <v-icon>print</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Print Table`) }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin: 0; height: 30px; width: 30px"
                  fab
                  :loading="loadTheExportBtn"
                  flat
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('export')"
                >
                  <v-icon>fa-file-excel</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Export Table`) }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>

        <v-layout row justify-end>
          <v-flex xs3 pa-2 shrink>
            <v-autocomplete
              v-if="hasPermissions(['outlet_select'])"
              v-model="outlet_id"
              :label="`${$t('Outlet')} *`"
              hide-details
              :items="outletsList"
              @input="updateDatatableResult()"
              data-vv-name="outlet"
              :error="errors.has('outlet')"
              v-validate="'required'"
            />
          </v-flex>
          <v-flex pa-2 shrink>
            <v-menu v-model="dateMenu" full-width max-width="290" :close-on-content-click="false">
              <v-text-field
                slot="activator"
                :value="date"
                :label="$t('Date')"
                prepend-inner-icon="event"
                hide-details
                readonly
                name="date"
                :error="errors.has('date')"
                v-validate="'required|date_format:yyyy-MM-dd'"
              />
              <v-date-picker @change="dateMenu = false" v-model="date" @input="updateDatatableResult()" :max="today" />
            </v-menu>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card px-4 pb-4>
      <v-data-table
        :headers="headers"
        :items="resultData"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.account_chart_id }}</td>-->
          <td>{{ $t(props.item.account_chart_name.trim()) }}</td>
          <td>{{ props.item.debit | currency }}</td>
          <td>{{ props.item.credit | currency }}</td>
          <td>{{ props.item.balance | currency }}</td>

          <td>
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('Account Detail') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Ledger Account Detail`) }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>
    </v-card>

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Axios from 'axios'
import * as helpers from '@/mixins/helperFunctions'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue')
  },

  data() {
    return {
      dialogs: {
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      outlet_id: 0,
      dateMenu: false,
      today: this.$moment()
        .format()
        .substr(0, 10),
      date: this.$moment()
        .format()
        .substr(0, 10),
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'account_chart_id',
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'account_chart_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Account Name'), value: 'account_chart_name' },
        {
          text: `${this.$t('Debit')} (${localStorage.systemCurrency})`,
          value: 'debit'
        },
        {
          text: `${this.$t('Credit')} (${localStorage.systemCurrency})`,
          value: 'credit'
        },
        {
          text: `${this.$t('Balance')} (${localStorage.systemCurrency})`,
          value: 'balance'
        }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('General Ledger Report'),
      icon: 'fa-chart-line'
    })

    // if (this.hasActionsMenuPermissions()) {
    this.headers.push({
      text: this.$t('datatable-actions'),
      value: '',
      sortable: false
    })
    // }

    this.fetchOutletList()
    this.updateDatatableResult()
  },

  computed: {
    ...mapGetters({
      resultData: 'generalLedger/getLedgerList',
      outletsList: 'outlets/getFullReportOutletsList'
    })
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'generalLedger/updateLedgerList',
      fetchOutletList: 'outlets/setFullOutletsList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get(`report_general_ledger`, {
        params: {
          is_paginated: 0,
          search: this.search,
          outlet_id: this.outlet_id,
          date: this.date
        }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        outlet_id: this.outlet_id,
        date: this.date,
        search: this.search
      }
      this.updateResult(payload)
        .then(() => {
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
      this.updateDatatableResult()
    },

    viewRecord(item) {
      this.$router.push({
        name: 'ledger-details',
        query: {
          id: item.account_chart_id,
          balance: item.balance,
          name: item.account_chart_name,
          date: this.date,
          outlet_id: this.outlet_id
        }
      })
    }
  }
}
</script>

<style scoped />
